// App Layout with Sidebar Navigation
// Based on brain pattern: App Layout & Navigation Pattern
import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
  Select,
  FormControl,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  ConfirmationNumber,
  Article,
  Lightbulb,
  Settings,
  Logout,
  Business,
  People,
  PersonOutline,
  BarChart,
  AdminPanelSettings,
  ExpandLess,
  ExpandMore,
  Palette,
  Code,
  Notifications,
  Add,
  FolderOpen,
} from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { usePermissions } from '@/hooks/usePermissions';
import { AddProjectDialog } from '@/components/projects/AddProjectDialog';

const DRAWER_WIDTH = 260;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  permission?: string;
  children?: NavItem[];
}

const mainNavItems: NavItem[] = [
  { label: 'Dashboard', path: '/app/dashboard', icon: <Dashboard /> },
  {
    label: 'Tickets',
    path: '/app/tickets',
    icon: <ConfirmationNumber />,
    permission: 'ticket:view',
  },
  {
    label: 'Knowledge Base',
    path: '/app/articles',
    icon: <Article />,
    permission: 'article:view',
  },
  {
    label: 'Feature Requests',
    path: '/app/features',
    icon: <Lightbulb />,
    permission: 'feature:view',
  },
  {
    label: 'End Users',
    path: '/app/users',
    icon: <PersonOutline />,
    permission: 'ticket:view',
  },
  {
    label: 'Analytics',
    path: '/app/analytics',
    icon: <BarChart />,
    permission: 'analytics:view',
  },
];

const settingsNavItems: NavItem[] = [
  {
    label: 'General',
    path: '/app/settings',
    icon: <Settings />,
    permission: 'org:settings',
  },
  {
    label: 'Projects',
    path: '/app/settings/projects',
    icon: <FolderOpen />,
    permission: 'project:view',
  },
  {
    label: 'Team',
    path: '/app/settings/team',
    icon: <People />,
    permission: 'org:members',
  },
  {
    label: 'Branding',
    path: '/app/settings/branding',
    icon: <Palette />,
    permission: 'org:settings',
  },
  {
    label: 'Widget',
    path: '/app/settings/widget',
    icon: <Code />,
    permission: 'project:settings',
  },
  {
    label: 'Notifications',
    path: '/app/settings/notifications',
    icon: <Notifications />,
    permission: 'org:settings',
  },
];

export const AppLayout = () => {
  const { user, signOut, isSuperAdmin } = useAuth();
  const { organization, organizations, project, projects, setCurrentOrganization, setCurrentProject, loadingOrg } =
    useOrganization();
  const { can, plan } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(location.pathname.includes('/settings'));
  const [addProjectDialogOpen, setAddProjectDialogOpen] = useState(false);

  // Redirect to onboarding if user has no organizations
  // But respect the onboardingCompleted flag to prevent redirect loops
  useEffect(() => {
    if (loadingOrg) return;

    // Check if onboarding was just completed - give time for data to sync
    const onboardingCompleted = localStorage.getItem('onboardingCompleted');

    if (organizations.length > 0) {
      // Organizations loaded successfully, clear the onboarding flag
      if (onboardingCompleted) {
        localStorage.removeItem('onboardingCompleted');
      }
      return;
    }

    // No organizations and not just completed onboarding - redirect
    if (!onboardingCompleted) {
      navigate('/onboarding', { replace: true });
    }
    // If onboardingCompleted flag is set but orgs are still empty,
    // it means data hasn't synced yet - don't redirect, let OrganizationContext retry
  }, [loadingOrg, organizations, navigate]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const isActivePath = (path: string) => {
    if (path === '/app/dashboard') return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const filteredMainNav = mainNavItems.filter(
    (item) => !item.permission || can(item.permission)
  );

  const filteredSettingsNav = settingsNavItems.filter(
    (item) => !item.permission || can(item.permission)
  );

  const drawer = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        height: 'auto',
        background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
        color: 'white',
      }}
    >
      {/* Logo */}
      <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          component="img"
          src="/favicon.png"
          alt="SupportSpace"
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
          }}
        />
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          SupportSpace
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

      {/* Organization Selector */}
      {organizations.length > 0 && (
        <Box sx={{ px: 1.5, py: 0.5 }}>
          <FormControl fullWidth size="small">
            <Select
              value={organization?.id || ''}
              onChange={(e) => setCurrentOrganization(e.target.value)}
              displayEmpty
              sx={{
                color: 'white',
                '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
                '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' },
              }}
            >
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  <Business fontSize="small" sx={{ mr: 1 }} />
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Project Selector */}
      <Box sx={{ px: 1.5, pb: 0.5 }}>
        <FormControl fullWidth size="small">
          <Select
            value={project?.id || ''}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '__add_project__') {
                setAddProjectDialogOpen(true);
              } else if (value === '__manage_projects__') {
                navigate('/app/settings/projects');
                if (isMobile) setMobileOpen(false);
              } else {
                setCurrentProject(value);
              }
            }}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) return 'Select Project';
              const selectedProject = projects.find((p) => p.id === selected);
              return selectedProject?.name || 'Select Project';
            }}
            sx={{
              color: 'white',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
              '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' },
            }}
          >
            {projects.map((proj) => (
              <MenuItem key={proj.id} value={proj.id}>
                {proj.name}
              </MenuItem>
            ))}
            {projects.length > 0 && <Divider sx={{ my: 0.5 }} />}
            <MenuItem value="__add_project__" sx={{ color: 'primary.main' }}>
              <Add sx={{ mr: 1, fontSize: 18 }} /> Add Project
            </MenuItem>
            {projects.length > 0 && (
              <MenuItem value="__manage_projects__" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                <Settings sx={{ mr: 1, fontSize: 16 }} /> Manage Projects
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

      {/* Main Navigation */}
      <List sx={{ flex: 1, px: 1, py: 0.5 }} dense>
        {filteredMainNav.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={isActivePath(item.path)}
              onClick={() => handleNavClick(item.path)}
              sx={{
                borderRadius: 1.5,
                py: 0.5,
                color: 'rgba(255,255,255,0.7)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.08)',
                  color: 'white',
                },
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                  },
                  '& .MuiListItemIcon-root': { color: 'white' },
                },
                '& .MuiListItemIcon-root': {
                  color: 'rgba(255,255,255,0.6)',
                  transition: 'color 0.2s ease',
                },
                '&:hover .MuiListItemIcon-root': { color: 'white' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}

        {/* Settings Section */}
        {filteredSettingsNav.length > 0 && (
          <>
            <ListItem disablePadding sx={{ mt: 0.5 }}>
              <ListItemButton
                onClick={() => setSettingsOpen(!settingsOpen)}
                sx={{
                  borderRadius: 1.5,
                  py: 0.5,
                  color: 'rgba(255,255,255,0.7)',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.08)',
                    color: 'white',
                  },
                  '& .MuiListItemIcon-root': { color: 'rgba(255,255,255,0.6)' },
                  '&:hover .MuiListItemIcon-root': { color: 'white' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Settings />
                </ListItemIcon>
                <ListItemText primary="Settings" />
                {settingsOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {filteredSettingsNav.map((item) => (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton
                      selected={location.pathname === item.path}
                      onClick={() => handleNavClick(item.path)}
                      sx={{
                        pl: 4,
                        py: 0.375,
                        borderRadius: 1.5,
                        color: 'rgba(255,255,255,0.6)',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.05)',
                          color: 'white',
                        },
                        '&.Mui-selected': {
                          bgcolor: 'rgba(59, 130, 246, 0.2)',
                          color: '#60a5fa',
                          '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.25)' },
                          '& .MuiListItemIcon-root': { color: '#60a5fa' },
                        },
                        '& .MuiListItemIcon-root': { color: 'rgba(255,255,255,0.5)' },
                        '&:hover .MuiListItemIcon-root': { color: 'white' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </List>

      {/* Plan Badge */}
      <Box sx={{ px: 1.5, pb: 1 }}>
        <Box
          sx={{
            p: 0.75,
            borderRadius: 1.5,
            background:
              plan === 'free'
                ? 'rgba(255,255,255,0.05)'
                : plan === 'pro'
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)'
                  : 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
            border: '1px solid',
            borderColor:
              plan === 'free'
                ? 'rgba(255,255,255,0.1)'
                : plan === 'pro'
                  ? 'rgba(59, 130, 246, 0.3)'
                  : 'rgba(139, 92, 246, 0.3)',
            textAlign: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.5)',
              display: 'block',
              mb: 0.5,
              textTransform: 'uppercase',
              letterSpacing: 1,
              fontSize: '0.65rem',
            }}
          >
            Current Plan
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color:
                plan === 'free' ? 'rgba(255,255,255,0.8)' : plan === 'pro' ? '#60a5fa' : '#a78bfa',
            }}
          >
            {plan.charAt(0).toUpperCase() + plan.slice(1)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          borderBottom: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.06)',
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          backdropFilter: 'blur(12px)',
          bgcolor: 'rgba(255, 255, 255, 0.9)',
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
          )}

          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600, color: 'text.primary' }} noWrap>
            {project?.name || organization?.name || 'SupportSpace'}
          </Typography>

          {isSuperAdmin && (
            <Chip
              icon={<AdminPanelSettings />}
              label="Super Admin"
              size="small"
              sx={{
                mr: 2,
                background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                color: 'white',
                fontWeight: 500,
                '& .MuiChip-icon': { color: 'white' },
              }}
              onClick={() => navigate('/super-admin')}
            />
          )}

          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              p: 0.5,
              border: '2px solid transparent',
              '&:hover': { borderColor: 'primary.light' },
            }}
          >
            <Avatar
              src={user?.photoURL || undefined}
              sx={{
                width: 36,
                height: 36,
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              }}
            >
              {user?.displayName?.[0] || user?.email?.[0]}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate('/app/settings/profile');
              }}
            >
              Profile Settings
            </MenuItem>
            {isSuperAdmin && (
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  navigate('/app/super-admin/customers');
                }}
              >
                <AdminPanelSettings sx={{ mr: 1 }} fontSize="small" /> Customer Administrator
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={handleSignOut}>
              <Logout sx={{ mr: 1 }} fontSize="small" /> Sign Out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              border: 'none',
              boxShadow: '4px 0 24px rgba(0, 0, 0, 0.12)',
              background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
              overflowY: 'auto',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '50%',
            height: '300px',
            background: 'radial-gradient(ellipse at top right, rgba(59, 130, 246, 0.08) 0%, transparent 70%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}
        <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
          <Outlet />
        </Box>
      </Box>

      {/* Add Project Dialog */}
      <AddProjectDialog
        open={addProjectDialogOpen}
        onClose={() => setAddProjectDialogOpen(false)}
      />
    </Box>
  );
};
